import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MuiSelect, MuiTypography, OptionsDataTypeWithNode } from "..";
import { ImagePath } from "../../api/services/pageContent/models";
import { useSaveUserPreferredLanguageMutation } from "../../api/services/user/userApi";
import { TIMEOUT_200 } from "../../helpers/constants";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import { reloadPage, replaceHashWithValue } from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { userPreferredLanguageSelector } from "../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { LanguageSelectionWrapper } from "./LanguageSelection.style";

const renderLabelWithImage = (
  imageDetail?: ImagePath,
  label?: string,
  isMobile?: boolean,
) => {
  if (!label) return null;

  return (
    <>
      {imageDetail && (
        <img
          alt={imageDetail.AltText}
          src={imageDetail.ImageUrl}
          style={{
            marginRight: 8,
            height: parseInt(imageDetail.Height),
            width: parseInt(imageDetail.Width),
          }}
        />
      )}
      <MuiTypography component="span" className="h3 ellipsis">
        {label}
      </MuiTypography>
      {isMobile && <ArrowDropDownIcon fontSize="small" />}
    </>
  );
};

export const LanguageSelection = () => {
  const dispatch = useDispatch();
  const { fetchLanguageDropdownValues, fetchHeaderLabel } =
    useNavigationSiteCore();
  const { commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const preferredLanguage = useSelector(userPreferredLanguageSelector);
  const [selectedLanguage, setSelectedLanguage] = useState(preferredLanguage);
  const {
    mutateAsync: saveUserPreferredLanguage,
    isSuccess: isSuccessLanguage,
    isError: isErrorLanguage,
    isPending: isPendingLanguage,
  } = useSaveUserPreferredLanguageMutation();
  const { isMobile } = useMediaQueries();
  useEffect(() => {
    if (isSuccessLanguage) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchHeaderLabel(commonSiteCoreKeys.preferredLanguageSaved),
        }),
      );
      setTimeout(() => {
        reloadPage();
      }, TIMEOUT_200);
    }
    isErrorLanguage &&
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
  }, [isSuccessLanguage, isErrorLanguage]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPendingLanguage,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [isPendingLanguage]);

  const options = useMemo(
    () =>
      fetchLanguageDropdownValues().map((item) => ({
        ...item,
        label: renderLabelWithImage(item.imagePath, item.label),
      })) as OptionsDataTypeWithNode[],
    [fetchLanguageDropdownValues],
  );

  const handleLanguageChange = (value: string) => {
    setSelectedLanguage(value);
    saveUserPreferredLanguage(value);
  };

  const selectedLanguageLabel =
    fetchLanguageDropdownValues().find(
      (option) => option.value === selectedLanguage,
    )?.label ?? "";

  const ariaLabelText = commonARIALabel?.selectLanguageCurrentlySelected
    ? replaceHashWithValue(
        commonARIALabel?.selectLanguageCurrentlySelected,
        selectedLanguageLabel,
      )
    : `Select Language, currently ${selectedLanguageLabel}`;

  const renderSelectedValue = (value: unknown) => {
    const stringValue = value as string;

    const selected = fetchLanguageDropdownValues().find(
      (option) => option.value === stringValue,
    );
    if (!selected) return "";

    return renderLabelWithImage(
      selected.imagePath,
      isMobile ? selected.label.substring(0, 2) : selected.label,
      isMobile,
    );
  };

  return (
    <LanguageSelectionWrapper>
      <MuiSelect
        label=""
        value={selectedLanguage}
        onChange={(e) => handleLanguageChange(e.target.value as string)}
        options={options}
        className="header-language-select"
        id="header-language-select"
        inputProps={{
          "aria-label": ariaLabelText,
          title: ariaLabelText,
        }}
        tabIndex={0}
        requiredARIALabel={commonARIALabel.required}
        placeholder={commonLabels.selectPlaceHolder}
        renderValue={renderSelectedValue}
      />
    </LanguageSelectionWrapper>
  );
};
